import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';

import { store, history } from './helpers';
import App from './App/App';
import ErrorBoundary from './components/ErrorBoundary';
import { showToast } from './services/toast';
import { logger } from './utils/logger';
import { organizationActions } from './actions';
import {
    getObservabilityContext,
    initFaro,
    pushFaroEvent,
    isObservabilityEnabled,
    getAppName,
} from './observability';

function checkRequiredEnv() {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl || typeof apiUrl !== 'string' || apiUrl.trim() === '') {
        throw new Error(
            'REACT_APP_API_URL is not set. Please configure it in your .env file (e.g. REACT_APP_API_URL=https://api.example.com/).'
        );
    }
}

try {
    checkRequiredEnv();
} catch (e) {
    if (process.env.NODE_ENV === 'production') {
        throw e;
    }
    logger.warn('[Config]', e.message);
}

if (process.env.NODE_ENV !== 'production' && (!process.env.REACT_APP_SOCKET_URL || String(process.env.REACT_APP_SOCKET_URL).trim() === '')) {
    logger.warn('[Config] REACT_APP_SOCKET_URL is not set; real-time chat may not connect.');
}

function setupUnhandledRejectionHandler() {
    window.addEventListener('unhandledrejection', (event) => {
        const message = event.reason?.message || String(event.reason);
        logger.error('Unhandled promise rejection:', event.reason);
        showToast('Something went wrong. Please try again.', 'error');
        event.preventDefault();
    });
}

setupUnhandledRejectionHandler();

getObservabilityContext();
initFaro();

// Load customer branding early (public API) for titles, logo, login background.
store.dispatch(organizationActions.fetchBranding()).catch(() => {
    // Keep static fallbacks if branding API is unavailable.
});

if (isObservabilityEnabled() && history && typeof history.listen === 'function') {
    history.listen((location) => {
        if (location && location.pathname) {
            pushFaroEvent('route_change', { app_name: getAppName(), path: location.pathname });
        }
    });
}

const root = ReactDOM.createRoot(document.getElementById('app'));
const app = (
    <ErrorBoundary>
        <Provider store={store}>
            <App />
        </Provider>
    </ErrorBoundary>
);
// React 18 StrictMode intentionally double-invokes effects in development,
// which causes every page-level API request to fire twice on first mount.
root.render(app);
